import PlatformTypeBadge from '@boComponents/PlatformTypeBadge';
import { faCircleInfo, faEye } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Action from '@sharedComponents/Action';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { formatDate } from '@utils/date';

import rowStyle from '@sharedComponents/Row/style.module.css';

const EMPTY = '—';
const INFO_ICON_STYLE = { color: 'var(--text-color-neutral-tertiary)', fontSize: 14 };

// Platform URL with, on a second line, the platform name when it differs from the client name
// (a school of a group, a recette named after its environment…). An info icon flags a domain owned by the client.
const PlatformUrlCell = ({ item, t }) => {
    const showName = item.name && item.name !== item.client.name;
    return (
        <FlexBox flexDirection="column" gap="xxs">
            <FlexBox gap="xxs" alignItems="center">
                {item.url ? <Action action={`https://${item.url}`}>{item.url}</Action> : <Typography>{EMPTY}</Typography>}
                {item.domainOwner === 'CLIENT' && (
                    <Tooltip text={t('The domain is managed by the client.')} position="top" popupIcon={false}>
                        <FontAwesomeIcon icon={faCircleInfo} style={INFO_ICON_STYLE} />
                    </Tooltip>
                )}
            </FlexBox>
            {showName && (
                <Typography fontSize="s" color="neutral-tertiary">
                    {item.name}
                </Typography>
            )}
        </FlexBox>
    );
};

// Columns of a platform table, one row per platform: the "By platform" tab of the client list and the
// platforms tab of a client. `withClient: false` drops the client columns, redundant on a client's own page.
export const getPlatformColumns = (t, { onViewPlatform = () => {}, withClient = true } = {}) => [
    ...(withClient
        ? [
              {
                  key: 'clientName',
                  label: t('Client name'),
                  tdClassName: rowStyle.td,
                  render: (item) => <Typography fontWeight="medium">{item.client.name}</Typography>,
              },
              {
                  key: 'clientReference',
                  label: t('Client reference'),
                  tdClassName: rowStyle.td,
                  render: (item) => item.client.reference ?? EMPTY,
              },
          ]
        : []),
    {
        key: 'url',
        label: t('Platform URL'),
        tdClassName: rowStyle.td,
        render: (item) => <PlatformUrlCell item={item} t={t} />,
    },
    {
        key: 'type',
        label: t('Platform type'),
        tdClassName: rowStyle.td,
        render: (item) => <PlatformTypeBadge type={item.type} />,
    },
    {
        key: 'database',
        label: t('Database'),
        tdClassName: rowStyle.td,
        render: (item) => item.databaseName ?? EMPTY,
    },
    {
        key: 'status',
        label: t('Status'),
        tdClassName: rowStyle.td,
        render: (item) => <Tag color={item.active ? 'success' : 'disabled'}>{item.active ? t('Active') : t('Inactive')}</Tag>,
    },
    {
        key: 'createdAt',
        label: t('Created at'),
        tdClassName: rowStyle.td,
        render: (item) => (item.createdAt ? formatDate(item.createdAt) : EMPTY),
    },
    {
        key: 'actions',
        ariaLabel: t('Actions'),
        tdClassName: rowStyle.tdActions,
        pinned: true,
        render: (item) => (
            <Button ghost icon={faEye} action={() => onViewPlatform(item.client.uuid, item.uuid)} title={t('View platform')} />
        ),
    },
];
